import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { fmtDate } from "../../lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import Card from "../../components/Card";
import Switch from "../../components/Switch";
import { useAuth } from "../../context/AuthContext";
import { PasswordRules, isValidPassword } from "../ResetPassword";

export default function ClientAccount() {
  const [a, setA] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ email: "", phone: "", first_name: "", last_name: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [savingCoord, setSavingCoord] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Case « notifications par e-mail » de l'accueil guidé, modifiable à tout moment (décision du 15/10, question 6).
  const { user, setUser } = useAuth();
  const emailNotif = user?.preferences?.email_notifications !== false;
  const toggleEmailNotif = async () => {
    const next = !emailNotif;
    try {
      await api.put("/client/preferences", { email_notifications: next });
      setUser((u) => ({ ...u, preferences: { ...(u?.preferences || {}), email_notifications: next } }));
      toast.success(next ? "Notifications par e-mail activées" : "Notifications par e-mail désactivées");
    } catch { toast.error("Le réglage n'a pas pu être enregistré"); }
  };

  useEffect(() => { api.get("/client/account").then((r) => { setA(r.data); setForm({ email: r.data.email, phone: r.data.phone, first_name: r.data.first_name || "", last_name: r.data.last_name || "" }); }); }, []);
  if (!a) return null;

  // « Annuler » oublie la saisie : le formulaire revient aux coordonnées enregistrées (recette du 15/10, ANO-08).
  const cancelEdit = () => {
    setForm({ email: a.email, phone: a.phone, first_name: a.first_name || "", last_name: a.last_name || "" });
    setEdit(false);
  };
  const save = async () => {
    setSavingCoord(true);
    try { await api.put("/client/account", form); toast.success("Informations mises à jour"); setA({ ...a, ...form }); setEdit(false); }
    catch { toast.error("Échec de l'enregistrement"); }
    finally { setSavingCoord(false); }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    // Mêmes règles que le serveur et que les autres pages (recette du 15/10, ANO-07 ; décision de Mélilia, question 8).
    if (!isValidPassword(pwd.new_password)) return toast.error("Le mot de passe doit contenir au moins 8 caractères, dont une majuscule et un symbole.");
    if (pwd.new_password !== pwd.confirm) return toast.error("Les mots de passe ne correspondent pas");
    setSavingPwd(true);
    try {
      await api.post("/me/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Mot de passe mis à jour");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e2) { toast.error(e2.response?.data?.detail || "Échec de la mise à jour"); }
    finally { setSavingPwd(false); }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const r = await api.get("/client/account/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `mes-donnees-metara-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
    } catch { toast.error("Échec de l'export"); }
    finally { setExporting(false); }
  };

  return (
    <div className="max-w-2xl">
      <PageTitle overline="Profil" title="Mon Compte" />
      <div className="space-y-6">
        <Card title="Mes coordonnées"
          action={!edit && <button onClick={() => setEdit(true)} data-testid="account-edit" className="text-sm border border-marine/25 text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1.5"><Icons.Pencil size={16} /> Modifier</button>}>
          {(a.first_name || a.last_name) && !edit && <div className="text-lg font-display text-marine">{[a.first_name, a.last_name].filter(Boolean).join(" ")}</div>}
          <div className="text-sm text-marine/80 mb-3">{a.name}</div>
          {edit ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="account-first-name" className="inp" placeholder="Prénom" />
                <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="account-last-name" className="inp" placeholder="Nom" />
              </div>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="account-email" className="inp" placeholder="E-mail" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="account-phone" className="inp" placeholder="Téléphone" />
              <div className="flex justify-end gap-2">
                <button onClick={cancelEdit} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
                <button onClick={save} disabled={savingCoord} data-testid="account-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors disabled:opacity-50">{savingCoord ? "Enregistrement…" : "Enregistrer"}</button>
              </div>
            </div>
          ) : (
            <div className="space-y-1 text-sm text-marine">
              <div className="flex items-center gap-2"><Icons.Mail size={16} className="text-marine" /> {a.email}</div>
              <div className="flex items-center gap-2"><Icons.Phone size={16} className="text-marine" /> {a.phone || "—"}</div>
            </div>
          )}
          <div className="text-xs text-marine/80 mt-4">Entreprise Metara depuis le {fmtDate(a.client_since)}</div>
        </Card>

        <Card title="Changer le mot de passe">
          <form onSubmit={submitPwd} className="space-y-4">
            <label className="text-sm block">Mot de passe actuel<input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="account-current-password" className="inp mt-1" required /></label>
            <label className="text-sm block">Nouveau mot de passe<input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="account-new-password" className="inp mt-1" required /></label>
            <PasswordRules value={pwd.new_password} />
            <label className="text-sm block">Confirmer le nouveau mot de passe<input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="account-confirm-password" className="inp mt-1" required /></label>
            <div className="flex justify-end"><button disabled={savingPwd} data-testid="account-password-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors disabled:opacity-50">{savingPwd ? "Enregistrement…" : "Mettre à jour"}</button></div>
          </form>
        </Card>

        <Card title="Notifications par e-mail">
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm text-marine/80">Recevoir par e-mail les avis de suivi : messages de l'agence, livrables, réponses à vos demandes, étapes, formulaires et publications à valider. Les e-mails liés à votre compte, à la facturation et aux rendez-vous vous parviennent toujours.</p>
            <Switch checked={emailNotif} onChange={toggleEmailNotif} testid="account-email-notifications" title="Recevoir les notifications par e-mail" />
          </div>
        </Card>

        <Card title="Mes données">
          <p className="text-sm text-marine/80 mb-4">Téléchargez une copie de vos données, telles que vous les voyez dans votre espace : votre fiche, vos contacts, vos projets (étapes, conversation, facturation), la liste de vos fichiers, vos demandes, vos réponses aux formulaires, votre charte et vos publications. Les fichiers eux-mêmes se téléchargent depuis votre espace.</p>
          <button onClick={exportData} disabled={exporting} data-testid="account-export-data"
            className="text-sm border border-marine/25 text-marine rounded-sm px-4 py-2 hover:bg-marine hover:text-white transition-colors disabled:opacity-50 flex items-center gap-1.5">
            <Icons.Download size={16} /> {exporting ? "Préparation…" : "Télécharger mes données"}
          </button>
        </Card>
      </div>
    </div>
  );
}
